/**
 * One agent's memory, and what it just reached for.
 *
 * Every dot is one remembered fact, placed near the facts it resembles. The
 * dots never move. What moves is which of them light up, and that is the only
 * animated thing on this panel because it is the only thing actually measured:
 * `rowsTouched` comes back from the retrieval on every turn.
 *
 * WHAT THIS DELIBERATELY DOES NOT SHOW. Attention, activations, anything about
 * the model's interior. transformers.js exposes none of it, and a plausible
 * swirl standing in for it would be a ghost variable: a picture that animates
 * convincingly while corresponding to nothing. The interior of the model is not
 * available; the interior of the MEMORY is, exactly, every turn.
 *
 * HOW MUCH TO TRUST THE POSITIONS. Two components of a 512-dimensional
 * embedding carry 5.5% to 11.6% of the variance across the five shipped
 * lattices, measured. So nearness is indicative rather than definitive, and the
 * panel says so rather than letting a viewer read a tight cluster as certainty.
 *
 * Canvas rather than DOM: webdev holds 9,886 facts, and that many elements
 * would cost more to lay out than the model costs to run.
 */

import { useEffect, useRef } from "react";
import type { Projection } from "./pure/projection";

/** A retrieval, with when it happened, so it can fade. */
export type Touch = { rows: number[]; at: number };

/** How long a flare stays visible. Long enough to read across a slow turn. */
const FADE_MS = 9000;

export function AgentField({
  projection,
  touches,
  label,
  detail,
  tone,
  running,
}: {
  projection: Projection | null;
  touches: Touch[];
  label: string;
  detail: string;
  /** CSS colour for this side, matching its edge in the transcript. */
  tone: string;
  running: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const touchRef = useRef(touches);
  touchRef.current = touches;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !projection || projection.count === 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;
    let stopped = false;

    const draw = () => {
      if (stopped) return;
      // Hi-dpi without blur, re-read each frame because a resize between
      // frames is cheaper to absorb than to listen for.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const pad = 10;
      const rx = (w - pad * 2) / 2;
      const ry = (h - pad * 2) / 2;
      const cx = w / 2;
      const cy = h / 2;
      const at = (i: number) => [cx + projection.xy[i * 2] * rx, cy + projection.xy[i * 2 + 1] * ry];

      // The lattice at rest. One pass, one colour, no per-point state: this is
      // the bulk of the drawing and it must stay cheap at ten thousand points.
      ctx.fillStyle = "rgba(150, 160, 165, 0.20)";
      for (let i = 0; i < projection.count; i++) {
        const [x, y] = at(i);
        ctx.fillRect(x - 0.5, y - 0.5, 1.4, 1.4);
      }

      // What was reached for, newest brightest.
      const now = performance.now();
      let live = false;
      for (const t of touchRef.current) {
        const age = now - t.at;
        if (age > FADE_MS) continue;
        live = true;
        const a = 1 - age / FADE_MS;
        ctx.fillStyle = tone;
        ctx.globalAlpha = Math.max(0.12, a);
        for (const row of t.rows) {
          if (row < 0 || row >= projection.count) continue;
          const [x, y] = at(row);
          const r = 1.6 + a * 2.4;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
        // A halo on the freshest touch only, so the eye lands on what just
        // happened rather than on everything that ever did.
        if (a > 0.86) {
          ctx.globalAlpha = (a - 0.86) * 4;
          ctx.strokeStyle = tone;
          ctx.lineWidth = 1;
          for (const row of t.rows) {
            if (row < 0 || row >= projection.count) continue;
            const [x, y] = at(row);
            ctx.beginPath();
            ctx.arc(x, y, 7 + (1 - a) * 40, 0, Math.PI * 2);
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;

      // Only keep a frame loop while something is actually changing. A canvas
      // redrawing ten thousand points forever, to show a still image, is the
      // kind of thing that makes a laptop fan come on for no reason.
      if (live || running) raf = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
    };
  }, [projection, tone, running, touches]);

  const touchedRows = new Set(touches.flatMap((t) => t.rows));

  return (
    <figure className="agent-field">
      <figcaption>
        <span className="nm" style={{ color: tone }}>
          {label}
        </span>
        <span className="det">{detail}</span>
      </figcaption>
      {projection && projection.count > 0 ? (
        <canvas ref={canvasRef} />
      ) : (
        <div className="field-empty">laying out this memory</div>
      )}
      <figcaption className="foot">
        {touchedRows.size > 0
          ? `${touchedRows.size} of ${projection?.count ?? 0} reached for`
          : "nothing reached for yet"}
      </figcaption>
    </figure>
  );
}
